import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { LOCALES, LOCALE_LABELS, type Locale } from "../i18n/locale-context";
import { menuKeyIndex } from "../lib/menuKeys";
import { CheckIcon, ChevronDownIcon, GlobeIcon } from "./icons";

/**
 * One globe button with a dropdown of the three languages, each in its own
 * spelling. Website: items are links to that language's page (`hrefFor`).
 * Portal: items are buttons that call `onSelect`.
 * `compact`: "auto" shows only the globe below 640px, "lg" below 1024px, "always" never shows the name.
 * `align`: which edge the menu opens from. `direction`: open below or above.
 * `tone`: "dark" styles the trigger for a navy bar (the website header).
 */
export function LanguageMenu({
  current,
  label,
  onSelect,
  hrefFor,
  compact = "auto",
  align = "right",
  direction = "down",
  tone = "light",
}: {
  current: Locale;
  label: string;
  onSelect?: (l: Locale) => void;
  hrefFor?: (l: Locale) => string;
  compact?: "auto" | "lg" | "always";
  align?: "left" | "right";
  direction?: "down" | "up";
  tone?: "light" | "dark";
}) {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLElement | null)[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();
  const dark = tone === "dark";

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  useEffect(() => {
    if (open) itemRefs.current[focus]?.focus();
  }, [open, focus]);

  function openMenu() {
    setFocus(Math.max(0, LOCALES.indexOf(current)));
    setOpen(true);
  }

  function onButtonKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openMenu();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocus(LOCALES.length - 1);
      setOpen(true);
    }
  }

  function onMenuKey(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape" || e.key === "Tab") {
      // Move focus back to the trigger first; for Tab, don't preventDefault
      // so the browser's default tab order continues from the trigger.
      buttonRef.current?.focus();
      setOpen(false);
      return;
    }
    if (e.key === " ") {
      // Space doesn't activate an <a> by default (only Enter does); trigger
      // the click ourselves so link items behave like the button items.
      const target = e.target as HTMLElement;
      if (target.tagName === "A") {
        e.preventDefault();
        target.click();
      }
      return;
    }
    const next = menuKeyIndex(e.key, focus, LOCALES.length);
    if (next !== null) {
      e.preventDefault();
      setFocus(next);
    }
  }

  const nameClass = compact === "always" ? "sr-only" : compact === "lg" ? "sr-only lg:not-sr-only" : "sr-only sm:not-sr-only";
  const pos = [align === "right" ? "right-0" : "left-0", direction === "down" ? "top-full mt-2" : "bottom-full mb-2"].join(" ");

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onButtonKey}
        className={`inline-flex h-10 min-w-10 items-center justify-center gap-1.5 rounded-full border px-2.5 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:px-3 ${
          dark
            ? "border-white/15 bg-white/5 text-slate-100 hover:border-white/30 focus-visible:outline-white"
            : "border-line bg-white text-stone-800 hover:border-stone-300 focus-visible:outline-brand-500"
        }`}
      >
        <GlobeIcon className={`h-[18px] w-[18px] ${dark ? "text-slate-300" : "text-stone-600"}`} />
        {/* Always in the accessible name (e.g. "Language: English"), even
            below sm where the visible name span collapses to sr-only. */}
        <span className="sr-only">{label}: </span>
        <span className={nameClass}>{LOCALE_LABELS[current]}</span>
        <ChevronDownIcon className={`hidden h-3.5 w-3.5 ${dark ? "text-slate-400" : "text-stone-400"} ${compact === "always" ? "" : compact === "lg" ? "lg:block" : "sm:block"}`} />
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          className={`absolute ${pos} z-50 min-w-[180px] rounded-xl border border-line bg-white p-1.5 shadow-[0_12px_30px_rgba(28,25,23,0.12)]`}
        >
          {LOCALES.map((l, i) => {
            const on = l === current;
            const cls = `flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left text-sm text-stone-900 outline-none hover:bg-stone-100 focus-visible:bg-stone-100 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500 ${on ? "bg-brand-50 font-semibold" : ""}`;
            const inner = (
              <>
                <span className="flex w-4 justify-center text-brand-600">{on && <CheckIcon className="h-3.5 w-3.5" />}</span>
                <span lang={l}>{LOCALE_LABELS[l]}</span>
              </>
            );
            return hrefFor ? (
              <a
                key={l}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                role="menuitemradio"
                aria-checked={on}
                aria-current={on ? "true" : undefined}
                href={hrefFor(l)}
                hrefLang={l}
                tabIndex={i === focus ? 0 : -1}
                onMouseEnter={() => setFocus(i)}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                  if (on) {
                    // Already on this language: don't reload the page, just close.
                    e.preventDefault();
                    setOpen(false);
                    return;
                  }
                  onSelect?.(l);
                  setOpen(false);
                }}
                className={cls}
              >
                {inner}
              </a>
            ) : (
              <button
                key={l}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={on}
                tabIndex={i === focus ? 0 : -1}
                onMouseEnter={() => setFocus(i)}
                onClick={() => {
                  onSelect?.(l);
                  setOpen(false);
                  buttonRef.current?.focus();
                }}
                className={cls}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
